import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import { useDebounce } from '../../utils/useServerFiltering';
import {
  SearchableContactDropdownProps,
  ContactOption,
  MIN_SEARCH_LENGTH,
  SEARCH_DEBOUNCE_DELAY,
  MAX_SEARCH_RESULTS
} from './config';

const SearchableContactDropdown: React.FC<SearchableContactDropdownProps> = ({
  value,
  onChange,
  placeholder = "Search contacts...",
  className = '',
  disabled = false,
  allowNone = true,
  noneLabel = 'All Contacts',
  clientId
}) => {
  const supabase = useSupabaseClient();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [displayText, setDisplayText] = useState('');
  const [contacts, setContacts] = useState<ContactOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const itemsContainerRef = useRef<HTMLDivElement>(null);
  const fetchedValueRef = useRef<number | string | null | undefined>(null);

  const debouncedSearchTerm = useDebounce(searchTerm, SEARCH_DEBOUNCE_DELAY);

  useEffect(() => {
    const performSearch = async () => {
      const hasSearchTerm = debouncedSearchTerm.length >= MIN_SEARCH_LENGTH;
      const shouldFetchClientContacts = !!clientId && isOpen;

      if (!hasSearchTerm && !shouldFetchClientContacts) {
        setContacts([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const params: Record<string, unknown> = {
          search_term: hasSearchTerm ? debouncedSearchTerm : null,
          limit_results: MAX_SEARCH_RESULTS
        };
        if (clientId) {
          params.filter_client_id = clientId;
        }

        const { data: results, error } = await supabase
          .rpc('search_contacts', params);

        if (error) {
          setContacts([]);
        } else {
          setContacts(results || []);
        }
      } catch {
        setContacts([]);
      } finally {
        setLoading(false);
      }
    };

    performSearch();
  }, [debouncedSearchTerm, clientId, isOpen, supabase]);

  const dropdownItems = useMemo(() => {
    return contacts.map(contact => ({
      id: contact.id,
      label: `${contact.first_name} ${contact.last_name}`,
      sublabel: contact.company_name,
      value: contact.id.toString()
    }));
  }, [contacts]);

  const hasClientPreload = !!clientId;

  const visibleItems = useMemo(() => {
    const items: Array<{ id: string | number; label: string; sublabel?: string; value: string | null }> = [];
    const hasSearchTerm = searchTerm.length >= MIN_SEARCH_LENGTH;
    const showResults = hasSearchTerm || hasClientPreload;

    if (showResults && !loading) {
      if (allowNone) {
        items.push({ id: 'none', label: noneLabel, value: null });
      }
      dropdownItems.forEach(item => items.push(item));
    } else if (!showResults) {
      if (allowNone) {
        items.push({ id: 'none', label: noneLabel, value: null });
      }
    }

    return items;
  }, [searchTerm, allowNone, noneLabel, loading, dropdownItems, hasClientPreload]);

  useEffect(() => {
    if (isOpen) {
      setHighlightedIndex(0);
    } else {
      setHighlightedIndex(-1);
    }
  }, [isOpen, visibleItems.length]);

  useEffect(() => {
    if (isOpen && highlightedIndex >= 0 && itemsContainerRef.current) {
      const container = itemsContainerRef.current;
      const highlightedElement = container.querySelector(`[data-index="${highlightedIndex}"]`) as HTMLElement;
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [highlightedIndex, isOpen]);

  const handleSelect = useCallback((item: { id: string | number; label: string; value: string | null }) => {
    setDisplayText(item.label);
    fetchedValueRef.current = item.value;
    // The contact itself goes with the id, as the prop type promises, so a
    // caller can show the name without looking it up again.
    const contact = item.value != null
      ? contacts.find(c => String(c.id) === String(item.value)) ?? null
      : null;
    onChange(item.value, contact);
    setIsOpen(false);
    setSearchTerm('');
  }, [onChange, contacts]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (!disabled) setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < visibleItems.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : prev);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < visibleItems.length) {
          handleSelect(visibleItems[highlightedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setSearchTerm('');
        break;
      case 'Tab':
        setIsOpen(false);
        setSearchTerm('');
        break;
    }
  }, [isOpen, disabled, highlightedIndex, visibleItems, handleSelect]);

  useEffect(() => {
    if (value === null) {
      setDisplayText(noneLabel);
      fetchedValueRef.current = null;
      return;
    }

    if (fetchedValueRef.current === value) return;

    const fetchContactName = async () => {
      try {
        const contactId = typeof value === 'string' ? parseInt(value, 10) : value;

        const { data, error } = await supabase
          .from('contacts')
          .select('first_name, last_name')
          .eq('id', contactId)
          .single();

        if (error) {
          console.error('Error fetching contact name:', error);
          return;
        }

        if (data) {
          setDisplayText(`${data.first_name} ${data.last_name}`);
          fetchedValueRef.current = value;
        }
      } catch (err) {
        console.error('Error fetching contact name:', err);
      }
    };

    fetchContactName();
  }, [value, noneLabel, supabase]);

  const displayValue = displayText || placeholder;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={`dropdown-container ${className} ${disabled ? 'disabled' : ''}`} ref={dropdownRef}>
      <div
        className={`dropdown-trigger ${isOpen ? 'open' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        tabIndex={disabled ? -1 : 0}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls="contact-dropdown-listbox"
      >
        <div className="selected-value">
          {displayValue || placeholder}
        </div>
        <div className="dropdown-arrow">
          <svg
            width="12"
            height="8"
            viewBox="0 0 12 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={isOpen ? 'rotated' : ''}
          >
            <path
              d="M1 1.5L6 6.5L11 1.5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </div>

      {isOpen && (
        <div className="dropdown-menu">
          <div className="search-container">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search contacts..."
              className="search-input dropdown-search-input"
              onClick={(e) => e.stopPropagation()}
              autoFocus
            />
          </div>

          <div className="items-container" ref={itemsContainerRef} role="listbox" id="contact-dropdown-listbox">
            {loading && (
              <div className="no-results">Searching contacts...</div>
            )}

            {!loading && (
              <>
                {allowNone && (
                  <div
                    data-index={0}
                    className={`dropdown-item ${value === null ? 'selected' : ''} ${highlightedIndex === 0 ? 'highlighted' : ''}`}
                    onClick={() => handleSelect({ id: 'none', label: noneLabel, value: null })}
                    onMouseEnter={() => setHighlightedIndex(0)}
                    role="option"
                    aria-selected={value === null}
                  >
                    {noneLabel}
                  </div>
                )}

                {searchTerm.length > 0 && searchTerm.length < MIN_SEARCH_LENGTH && !hasClientPreload && (
                  <div className="no-results">
                    Type {MIN_SEARCH_LENGTH - searchTerm.length} more character{searchTerm.length === MIN_SEARCH_LENGTH - 1 ? '' : 's'} to search...
                  </div>
                )}

                {(searchTerm.length >= MIN_SEARCH_LENGTH || hasClientPreload) && (
                  dropdownItems.length > 0 ? (
                    dropdownItems.map((item, index) => {
                      const itemIndex = allowNone ? index + 1 : index;
                      return (
                        <div
                          key={item.id}
                          data-index={itemIndex}
                          className={`dropdown-item ${item.value === value ? 'selected' : ''} ${highlightedIndex === itemIndex ? 'highlighted' : ''}`}
                          onClick={() => handleSelect(item)}
                          onMouseEnter={() => setHighlightedIndex(itemIndex)}
                          role="option"
                          aria-selected={item.value === value}
                        >
                          <span className="contact-name">{item.label}</span>
                          {!clientId && item.sublabel && (
                            <span className="contact-client">{item.sublabel}</span>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    searchTerm.length > 0 ? (
                      <div className="no-results">No contacts found matching &ldquo;{searchTerm}&rdquo;</div>
                    ) : (
                      <div className="no-results">No contacts for this client</div>
                    )
                  )
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableContactDropdown;
